import React, { useCallback, useMemo, useState } from 'react';
import './Updates.css';
import './InteractiveElements.css';

// DiffText Component
function DiffText({ oldText = '', newText = '' }) {
  if (oldText === newText) {
    return <div className="diff-text same">{newText}</div>;
  }

  return (
    <div className="diff-text">
      <div className="diff-old">
        <span className="diff-label">Poprzednia wersja:</span>
        <div className="diff-content">{oldText}</div>
      </div>
      <div className="diff-new">
        <span className="diff-label">Nowa wersja:</span>
        <div className="diff-content">{newText}</div>
      </div>
    </div>
  );
}

function SpecsDiff({ oldSpecs = {}, newSpecs = {} }) {
  const keys = Array.from(new Set([...Object.keys(oldSpecs), ...Object.keys(newSpecs)]));
  return (
    <div className="diff-specs">
      {keys.map((k) => {
        const left = oldSpecs[k] || '';
        const right = newSpecs[k] || '';
        const type = left === right ? 'same' : !left ? 'added' : !right ? 'removed' : 'changed';
        return (
          <div key={k} className={`diff-row diff-${type}`}>
            <div className="diff-key">{k}</div>
            <div className="diff-val-left">{left}</div>
            <div className="diff-val-right">{right}</div>
          </div>
        );
      })}
    </div>
  );
}

function ImageCompare({ imageA, imageB, labelA = 'Wersja A', labelB = 'Wersja B' }) {
  const [compareMode, setCompareMode] = useState('slider'); // 'slider' lub 'side-by-side'
  const [sliderPosition, setSliderPosition] = useState(50);

  const handleSliderChange = (e) => {
    setSliderPosition(e.target.value);
  };

  if (compareMode === 'side-by-side') {
    return (
      <div className="image-compare">
        <div className="image-mode-toggle">
          <button 
            className={`mode-btn ${compareMode === 'slider' ? 'active' : ''}`}
            onClick={() => setCompareMode('slider')}
            title="Tryb przesuwania"
          >
            ⟷
          </button>
          <button 
            className={`mode-btn ${compareMode === 'side-by-side' ? 'active' : ''}`}
            onClick={() => setCompareMode('side-by-side')}
            title="Tryb obok siebie"
          >
            ⚏
          </button>
        </div>
        <div className="image-side-by-side">
          <div className="image-container-wrapper">
            <div className="image-container before-container">
              <img src={imageA} alt={labelA} className="version-image-side" />
              <div className="image-label before-label">
                <span className="legend-before">{labelA}</span>
              </div>
            </div>
          </div>
          <div className="image-container-wrapper">
            <div className="image-container after-container">
              <img src={imageB} alt={labelB} className="version-image-side" />
              <div className="image-label after-label">
                <span className="legend-after">{labelB}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Tryb z suwakiem
  return (
    <div className="image-compare">
      <div className="image-mode-toggle">
        <button 
          className={`mode-btn ${compareMode === 'slider' ? 'active' : ''}`}
          onClick={() => setCompareMode('slider')}
          title="Tryb przesuwania"
        >
          ⟷
        </button>
        <button 
          className={`mode-btn ${compareMode === 'side-by-side' ? 'active' : ''}`}
          onClick={() => setCompareMode('side-by-side')}
          title="Tryb obok siebie"
        >
          ⚏
        </button>
      </div>
      
      <div className="image-slider-container">
        <div className="image-stack">
          <img 
            src={imageA} 
            alt={labelA} 
            className="version-image before-image"
            style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
          />
          <img 
            src={imageB} 
            alt={labelB} 
            className="version-image after-image"
            style={{ clipPath: `inset(0 0 0 ${sliderPosition}%)` }}
          />
          
          {/* Linia podziału */}
          <div 
            className="slider-divider"
            style={{ left: `${sliderPosition}%` }}
          >
            <div className="slider-handle">
              <div className="slider-arrows">
                <span>‹</span>
                <span>›</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Suwak kontrolny */}
        <input
          type="range"
          min="0"
          max="100"
          value={sliderPosition}
          onChange={handleSliderChange}
          className="image-slider"
        />
        
        {/* Legenda */}
        <div className="image-legend positioned">
          <span className="legend-before">{labelA}</span>
          <span className="legend-after">{labelB}</span>
        </div>
      </div>
    </div>
  );
}

function normalizeText(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

function buildTreeMeta(tree) {
  const leafItems = [];
  const nodeMeta = {};

  const walk = (nodes, keyPath, labelPath) => {
    if (!nodes) return { leafCount: 0, versionCount: 0 };

    let leafCount = 0;
    let versionCount = 0;

    Object.keys(nodes).forEach((key) => {
      const node = nodes[key];
      const nextKeyPath = [...keyPath, key];
      const nodeLabel = node?.label || key;
      const nextLabelPath = [...labelPath, nodeLabel];
      const pathKey = nextKeyPath.join('/');

      const hasChildren = node?.children && Object.keys(node.children).length > 0;
      const hasVersions = Array.isArray(node?.versions) && node.versions.length > 0;

      let childCounts = { leafCount: 0, versionCount: 0 };
      if (hasChildren) {
        childCounts = walk(node.children, nextKeyPath, nextLabelPath);
      }

      let selfLeafCount = 0;
      let selfVersionCount = 0;
      if (hasVersions && !hasChildren) {
        selfLeafCount = 1;
        selfVersionCount = node.versions.length;
        leafItems.push({
          keyPath: nextKeyPath,
          pathKey,
          labelPath: nextLabelPath.join(' / '),
          versionsCount: node.versions.length,
        });
      }

      const totalLeafCount = childCounts.leafCount + selfLeafCount;
      const totalVersionCount = childCounts.versionCount + selfVersionCount;

      nodeMeta[pathKey] = {
        label: nodeLabel,
        leafCount: totalLeafCount,
        versionCount: totalVersionCount,
      };

      leafCount += totalLeafCount;
      versionCount += totalVersionCount;
    });

    return { leafCount, versionCount };
  };

  walk(tree, [], []);
  return { leafItems, nodeMeta };
}

function getLabelPathForKeyPath(tree, keyPath) {
  if (!tree || !Array.isArray(keyPath) || keyPath.length === 0) return [];

  const labels = [];
  let nodes = tree;

  for (let i = 0; i < keyPath.length; i++) {
    const key = keyPath[i];
    const node = nodes?.[key];
    if (!node) return [];
    labels.push(node.label || key);
    nodes = node.children;
  }

  return labels;
}

function VersionPicker({ title, versions, selectedId, onSelect, disabled }) {
  return (
    <div className={`version-picker-column ${disabled ? 'disabled' : ''}`}>
      <div className="version-picker-title">{title}</div>
      <div className="version-picker-list">
        {versions.map((v) => (
          <button
            key={v.id}
            type="button"
            className={`version-picker-item ${selectedId === v.id ? 'active' : ''}`}
            onClick={() => !disabled && onSelect(v.id)}
            disabled={disabled}
          >
            <div className="version-picker-item-id">{v.id}</div>
            <div className="version-picker-item-meta">{v.date}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Updates({ updates = [] }) {
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(null);

  const projectOptions = useMemo(() => {
    // Używamy bezpośrednio danych z updates, nie filtrujemy przez projects
    const source = Array.isArray(updates) ? updates : [];
    const options = source.map((u) => ({ 
      id: u.projectId, 
      name: u.projectId, // Tymczasowo używamy projectId jako nazwy
      description: `Projekt ${u.projectId}` 
    }));
    return options;
  }, [updates]);

  const [selectedProject, setSelectedProject] = useState('quickblocksui');
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [selectedSpec, setSelectedSpec] = useState(null);
  const [showVersionDetails, setShowVersionDetails] = useState(null);
  const [hoveredElement, setHoveredElement] = useState(null);
  const [versionPanelOpen, setVersionPanelOpen] = useState(false);

  // Adaptacyjna ścieżka filtrów (obsługa 2–8+ poziomów)
  const [filterPath, setFilterPath] = useState([]);
  const [selectedVersionA, setSelectedVersionA] = useState('');
  const [selectedVersionB, setSelectedVersionB] = useState('');

  // Dane projektu w nowym formacie (filters + tree)
  const dataset = useMemo(() => {
    const source = Array.isArray(updates) ? updates : [];
    return source.find((u) => u.projectId === selectedProject) || source[0] || null;
  }, [selectedProject, updates]);

  // Pomocnicza: pobierz węzeł drzewa na podstawie ścieżki filtrów
  const getNodeAtPath = useCallback((path) => {
    if (!dataset?.tree) return null;
    let node = dataset.tree;
    
    for (let i = 0; i < path.length; i++) {
      const key = path[i];
      if (!node || !node[key]) {
        return null;
      }
      
      // Przejdź do następnego poziomu
      node = node[key];
      
      // Jeśli to nie ostatni element ścieżki, przejdź do children
      if (i < path.length - 1) {
        if (node.children) {
          node = node.children;
        } else {
          return null;
        }
      }
    }
    return node;
  }, [dataset?.tree]);

  const handleSelectPath = (newPath) => {
    setFilterPath(newPath);
    setSelectedVersionA('');
    setSelectedVersionB('');
  };

  const treeMeta = useMemo(() => buildTreeMeta(dataset?.tree || null), [dataset]);
  const [treeSearch, setTreeSearch] = useState('');
  const [expandedNodes, setExpandedNodes] = useState({});
  const normalizedTreeSearch = useMemo(() => normalizeText(treeSearch), [treeSearch]);
  const treeSearchMatches = useMemo(() => {
    if (!normalizedTreeSearch) return [];
    return treeMeta.leafItems
      .filter((item) => normalizeText(item.labelPath).includes(normalizedTreeSearch))
      .slice(0, 80);
  }, [treeMeta, normalizedTreeSearch]);

  React.useEffect(() => {
    const rootKeys = Object.keys(dataset?.tree || {});
    const next = {};
    rootKeys.forEach((k) => {
      next[k] = true;
    });
    setExpandedNodes(next);
    setTreeSearch('');
  }, [dataset?.projectId, dataset?.tree]);

  const currentLabelPath = useMemo(() => {
    const labels = getLabelPathForKeyPath(dataset?.tree || null, filterPath);
    return labels.join(' / ');
  }, [dataset, filterPath]);

  const leafVersions = useMemo(() => {
    if (filterPath.length === 0) return [];
    const leafNode = getNodeAtPath(filterPath);
    if (!leafNode?.versions) return [];
    if (leafNode?.children && Object.keys(leafNode.children).length > 0) return [];
    return leafNode.versions;
  }, [filterPath, getNodeAtPath]);

  React.useEffect(() => {
    if (leafVersions.length === 0) return;

    const validIds = new Set(leafVersions.map((v) => v.id));
    const nextA = validIds.has(selectedVersionA) ? selectedVersionA : leafVersions[0]?.id || '';
    const nextB = validIds.has(selectedVersionB)
      ? selectedVersionB
      : leafVersions[1]?.id || leafVersions[0]?.id || '';

    if (nextA !== selectedVersionA) setSelectedVersionA(nextA);
    if (nextB !== selectedVersionB) setSelectedVersionB(nextB);
  }, [leafVersions, selectedVersionA, selectedVersionB]);

  // Dostępne wersje w wybranym liściu
  const availableVersions = useMemo(() => {
    if (filterPath.length === 0) return [];
    
    const leafNode = getNodeAtPath(filterPath);
    
    // Sprawdź czy to jest liść (ma versions ale nie ma children)
    if (leafNode?.versions && (!leafNode.children || Object.keys(leafNode.children).length === 0)) {
      const versions = leafNode.versions.map(v => ({ value: v.id, label: v.id }));
      return versions;
    }
    
    return [];
  }, [filterPath, getNodeAtPath]);

  // Pobierz obiekt wersji po ID z liścia
  const getVersionById = (versionId) => {
    if (filterPath.length === 0) return null;
    const leafNode = getNodeAtPath(filterPath);
    const versions = leafNode?.versions || [];
    return versions.find(v => v.id === versionId) || null;
  };

  // Determine which versions to compare
  let verA = null;
  let verB = null;

  // Sprawdź czy mamy wersje dostępne (jesteśmy w liściu)
  if (availableVersions.length > 0 && selectedVersionA) {
    verA = getVersionById(selectedVersionA);
  }
  if (availableVersions.length > 0 && selectedVersionB) {
    verB = getVersionById(selectedVersionB);
  }

  // Use first two versions as defaults if nothing selected and we have available versions
  if ((!verA || !verB) && availableVersions.length > 0) {
    const leafNode = getNodeAtPath(filterPath);
    const versions = leafNode?.versions || [];
    if (versions.length > 0) {
      verA = verA || versions[0];
      verB = verB || versions[1] || versions[0];
    }
  }

  const handleProjectChange = (projectId) => {
    setSelectedProject(projectId);
    // Reset wyborów
    setFilterPath([]);
    setSelectedVersionA('');
    setSelectedVersionB('');
    setProjectDropdownOpen(false);
  };

  const renderTreeNode = (nodes, keyPath = [], depth = 0) => {
    if (!nodes) return null;

    return Object.keys(nodes).map((key) => {
      const node = nodes[key];
      const nextKeyPath = [...keyPath, key];
      const nodeLabel = node?.label || key;
      const pathKey = nextKeyPath.join('/');

      const hasChildren = node?.children && Object.keys(node.children).length > 0;
      const isLeaf = Array.isArray(node?.versions) && node.versions.length > 0 && !hasChildren;
      const isSelected = filterPath.join('/') === pathKey;
      const isInPath = filterPath.length > 0 && pathKey === filterPath.slice(0, nextKeyPath.length).join('/');
      const isExpanded = !!expandedNodes[pathKey] || (depth === 0 && !!expandedNodes[key]);

      const meta = treeMeta.nodeMeta[pathKey];
      const countLabel =
        meta && meta.versionCount > 0
          ? `${meta.versionCount} wersje`
          : meta && meta.leafCount > 0
            ? `${meta.leafCount} pozycje`
            : '';

      return (
        <div key={pathKey} className={`tree-node depth-${depth} ${isSelected ? 'active' : ''} ${isInPath ? 'in-path' : ''}`}>
          <div className="tree-node-row">
            {hasChildren ? (
              <button
                type="button"
                className={`tree-node-toggle ${isExpanded ? 'open' : ''}`}
                onClick={() =>
                  setExpandedNodes((prev) => ({
                    ...prev,
                    [pathKey]: !isExpanded,
                    [depth === 0 ? key : pathKey]: !isExpanded,
                  }))
                }
              >
                ▸
              </button>
            ) : (
              <span className="tree-node-toggle-spacer" />
            )}
            <button
              type="button"
              className={`tree-node-label ${isLeaf ? 'leaf' : ''}`}
              onClick={() => handleSelectPath(nextKeyPath)}
            >
              <span className="tree-node-text">{nodeLabel}</span>
              {countLabel ? <span className="tree-node-count">{countLabel}</span> : null}
            </button>
          </div>

          {hasChildren && isExpanded ? (
            <div className="tree-node-children">{renderTreeNode(node.children, nextKeyPath, depth + 1)}</div>
          ) : null}
        </div>
      );
    });
  };



  // Obsługa przypadku gdy nie ma wersji do porównania - zachowujemy spójną strukturę layoutu
  if (!verA || !verB) {
    return (
      <div className="updates-page" data-page="updates">
        {/* Version Info Panel - Sliding from left */}
        <div className={`version-panel ${versionPanelOpen ? 'open' : ''}`}>
          <div className="version-panel-header">
            <h3>Informacje o wersjach</h3>
            <button 
              className="close-panel-btn"
              onClick={() => setVersionPanelOpen(false)}
            >
              ✕
            </button>
          </div>
          <div className="version-panel-content">
            <div className="no-versions-message">
              <h4>Brak wersji do wyświetlenia</h4>
              <p>Wybierz kategorię która zawiera wersje do porównania.</p>
            </div>
          </div>
        </div>

        {/* Main Layout Container */}
        <div className="updates-container">
          {/* Left Sidebar - Project Selector & Filters */}
          <div className="left-sidebar">
            {/* Project Selector */}
            <div className="project-selector-section">
              <div className="project-selector">
                <div className="project-btn-wrapper-Kx9mP2w">
                  <button 
                    className="project-btn-Kx9mP2w"
                    onClick={() => setProjectDropdownOpen(!projectDropdownOpen)}
                  >
                    {projectOptions.find(p => p.id === selectedProject)?.name || 'QuickBlocksUI'}
                    <span className={`dropdown-arrow ${projectDropdownOpen ? 'open' : ''}`}>▼</span>
                  </button>
                </div>
                
                {projectDropdownOpen && (
                  <div className="project-menu-wrapper-Qz7nR4t">
                    <div className="project-menu-Qz7nR4t">
                      {projectOptions.map((project) => (
                        <div 
                          key={project.id}
                          className={`project-option-Lm8vX1s ${selectedProject === project.id ? 'active' : ''}`}
                          onClick={() => handleProjectChange(project.id)}
                        >
                          <div className="project-name">{project.name}</div>
                          <div className="project-description">{project.description}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="filters-section">
              <h3>Filtry</h3>
              <div className="tree-filter">
                <div className="tree-search">
                  <input
                    className="tree-search-input"
                    type="text"
                    value={treeSearch}
                    onChange={(e) => setTreeSearch(e.target.value)}
                    placeholder="Szukaj (np. Przyciski, API, Instalacja)"
                  />
                </div>

                <div className="tree-current-row">
                  <div className="tree-current-path">
                    {currentLabelPath ? currentLabelPath : 'Wybierz kategorię z drzewa'}
                  </div>
                  <button
                    type="button"
                    className="tree-reset-btn"
                    onClick={() => handleSelectPath([])}
                    disabled={filterPath.length === 0}
                  >
                    Reset
                  </button>
                </div>

                {normalizedTreeSearch ? (
                  <div className="tree-search-results">
                    {treeSearchMatches.length === 0 ? (
                      <div className="tree-search-empty">Brak wyników</div>
                    ) : null}
                    {treeSearchMatches.map((item) => (
                        <button
                          key={item.pathKey}
                          type="button"
                          className={`tree-search-result ${filterPath.join('/') === item.pathKey ? 'active' : ''}`}
                          onClick={() => handleSelectPath(item.keyPath)}
                        >
                          <span className="tree-search-result-path">{item.labelPath}</span>
                          <span className="tree-search-result-count">{item.versionsCount} wersje</span>
                        </button>
                      ))}
                  </div>
                ) : (
                  <div className="tree-browser">{renderTreeNode(dataset?.tree || null)}</div>
                )}
              </div>

              <div className="version-picker">
                <VersionPicker
                  title="Wersja A"
                  versions={leafVersions}
                  selectedId={selectedVersionA}
                  onSelect={setSelectedVersionA}
                  disabled={leafVersions.length === 0}
                />
                <VersionPicker
                  title="Wersja B"
                  versions={leafVersions}
                  selectedId={selectedVersionB}
                  onSelect={setSelectedVersionB}
                  disabled={leafVersions.length === 0}
                />
              </div>
            </div>

            {/* Version Info Button */}
            <div className="version-info-trigger">
              <button 
                className="version-info-btn"
                onClick={() => setVersionPanelOpen(true)}
              >
                📋 Informacje o wersjach
              </button>
            </div>
          </div>

          {/* Center - Image Viewer */}
          <div className="center-content">
            <div className="image-viewer">
              <div className="no-versions-message">
                <h3>Brak wersji do porównania</h3>
                <p>Wybierz kategorię która zawiera wersje do porównania.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="updates-page" data-page="updates">
      {/* Version Info Panel - Sliding from left */}
      <div className={`version-panel ${versionPanelOpen ? 'open' : ''}`}>
        <div className="version-panel-header">
          <h3>Informacje o wersjach</h3>
          <button 
            className="close-panel-btn"
            onClick={() => setVersionPanelOpen(false)}
          >
            ✕
          </button>
        </div>
        <div className="version-panel-content">
          <div className="version-card-wrapper">
            <div 
              className={`version-card ${showVersionDetails === 'A' ? 'expanded' : ''}`}
              onClick={() => setShowVersionDetails(showVersionDetails === 'A' ? null : 'A')}
            >
              <h4>Wersja {verA.id}</h4>
              <p className="version-date">{verA.date}</p>
              <p className="version-description">{verA.description}</p>
              {showVersionDetails === 'A' && (
                <div className="version-details">
                  <h5>Szczegółowe specyfikacje:</h5>
                  {Object.entries(verA.specs).map(([key, value]) => (
                    <div key={key} className="spec-detail">
                      <strong>{key}:</strong> {value}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="version-card-wrapper">
            <div 
              className={`version-card ${showVersionDetails === 'B' ? 'expanded' : ''}`}
              onClick={() => setShowVersionDetails(showVersionDetails === 'B' ? null : 'B')}
            >
              <h4>Wersja {verB.id}</h4>
              <p className="version-date">{verB.date}</p>
              <p className="version-description">{verB.description}</p>
              {showVersionDetails === 'B' && (
                <div className="version-details">
                  <h5>Szczegółowe specyfikacje:</h5>
                  {Object.entries(verB.specs).map(([key, value]) => (
                    <div key={key} className="spec-detail">
                      <strong>{key}:</strong> {value}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Layout Container */}
      <div className="updates-container">
        {/* Left Sidebar - Project Selector & Filters */}
        <div className="left-sidebar">
          {/* Project Selector */}
          <div className="project-selector-section">
            <div className="project-selector">
              <div className="project-btn-wrapper-Kx9mP2w">
                <button 
                  className="project-btn-Kx9mP2w"
                  onClick={() => setProjectDropdownOpen(!projectDropdownOpen)}
                >
                  {projectOptions.find(p => p.id === selectedProject)?.name || 'QuickBlocksUI'}
                  <span className={`dropdown-arrow ${projectDropdownOpen ? 'open' : ''}`}>▼</span>
                </button>
              </div>
              
              {projectDropdownOpen && (
                <div className="project-menu-wrapper-Qz7nR4t">
                  <div className="project-menu-Qz7nR4t">
                    {projectOptions.map((project) => (
                      <div 
                        key={project.id}
                        className={`project-option-Lm8vX1s ${selectedProject === project.id ? 'active' : ''}`}
                        onClick={() => handleProjectChange(project.id)}
                      >
                        <div className="project-name">{project.name}</div>
                        <div className="project-description">{project.description}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="filters-section">
            <h3>Filtry</h3>
            <div className="tree-filter">
              <div className="tree-search">
                <input
                  className="tree-search-input"
                  type="text"
                  value={treeSearch}
                  onChange={(e) => setTreeSearch(e.target.value)}
                  placeholder="Szukaj (np. Przyciski, API, Instalacja)"
                />
              </div>

              <div className="tree-current-row">
                <div className="tree-current-path">
                  {currentLabelPath ? currentLabelPath : 'Wybierz kategorię z drzewa'}
                </div>
                <button
                  type="button"
                  className="tree-reset-btn"
                  onClick={() => handleSelectPath([])}
                  disabled={filterPath.length === 0}
                >
                  Reset
                </button>
              </div>

              {normalizedTreeSearch ? (
                <div className="tree-search-results">
                  {treeSearchMatches.length === 0 ? (
                    <div className="tree-search-empty">Brak wyników</div>
                  ) : null}
                  {treeSearchMatches.map((item) => (
                      <button
                        key={item.pathKey}
                        type="button"
                        className={`tree-search-result ${filterPath.join('/') === item.pathKey ? 'active' : ''}`}
                        onClick={() => handleSelectPath(item.keyPath)}
                      >
                        <span className="tree-search-result-path">{item.labelPath}</span>
                        <span className="tree-search-result-count">{item.versionsCount} wersje</span>
                      </button>
                    ))}
                </div>
              ) : (
                <div className="tree-browser">{renderTreeNode(dataset?.tree || null)}</div>
              )}
            </div>

            <div className="version-picker">
              <VersionPicker
                title="Wersja A"
                versions={leafVersions}
                selectedId={selectedVersionA}
                onSelect={setSelectedVersionA}
                disabled={leafVersions.length === 0}
              />
              <VersionPicker
                title="Wersja B"
                versions={leafVersions}
                selectedId={selectedVersionB}
                onSelect={setSelectedVersionB}
                disabled={leafVersions.length === 0}
              />
            </div>
          </div>

          {/* Version Info Button */}
          <div className="version-info-trigger">
            <button 
              className="version-info-btn"
              onClick={() => setVersionPanelOpen(true)}
            >
              📋 Informacje o wersjach
            </button>
          </div>
        </div>

        {/* Center - Image Viewer */}
        <div className="center-content">
          <div className="image-viewer">
            <ImageCompare 
              imageA={verA?.image}
              imageB={verB?.image}
              labelA={verA?.id}
              labelB={verB?.id}
            />
          </div>
        </div>
      </div>

      {/* Bottom Panel - Descriptions & Changes */}
      <div className="bottom-panel">
        <div className="specs-comparison">
          <h3>Porównanie specyfikacji 
            <span className="info-tooltip" 
                  onMouseEnter={() => setHoveredElement('specs-info')}
                  onMouseLeave={() => setHoveredElement(null)}>
              {hoveredElement === 'specs-info' && (
                <div className="tooltip-content">
                  Kliknij na dowolną specyfikację aby zobaczyć szczegóły
                </div>
              )}
            </span>
          </h3>
          <div onClick={(e) => {
            const specKey = e.target.closest('.diff-key')?.textContent;
            if (specKey) {
              setSelectedSpec({
                key: specKey,
                oldValue: verA.specs[specKey] || 'Brak',
                newValue: verB.specs[specKey] || 'Brak',
                project: selectedProject
              });
            }
          }}>
            <SpecsDiff oldSpecs={verA.specs} newSpecs={verB.specs} />
          </div>
        </div>

        <div className="notes-comparison">
          <h3>Notatki zmian
            <span className="info-tooltip"
                  onMouseEnter={() => setHoveredElement('notes-info')}
                  onMouseLeave={() => setHoveredElement(null)}>
              {hoveredElement === 'notes-info' && (
                <div className="tooltip-content">
                  Porównanie tekstowe zmian między wersjami
                </div>
              )}
            </span>
          </h3>
          <DiffText oldText={verA.notes} newText={verB.notes} />
        </div>
      </div>

      {/* Modal for Spec Details */}
      {selectedSpec && (
        <div className="spec-modal-overlay" onClick={() => setSelectedSpec(null)}>
          <div className="spec-modal" onClick={(e) => e.stopPropagation()}>
            <div className="spec-modal-header">
              <h3>Szczegóły specyfikacji: {selectedSpec.key}</h3>
              <button className="close-button" onClick={() => setSelectedSpec(null)}>✕</button>
            </div>
            <div className="spec-modal-content">
              <div className="spec-comparison-detail">
                <div className="spec-old">
                  <h4>Poprzednia wersja ({verA.id})</h4>
                  <p>{selectedSpec.oldValue}</p>
                </div>
                <div className="spec-new">
                  <h4>Nowa wersja ({verB.id})</h4>
                  <p>{selectedSpec.newValue}</p>
                </div>
              </div>
              <div className="spec-context">
                <h4>Kontekst projektu</h4>
                <p>{projectOptions.find(p => p.id === selectedProject)?.description}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
